import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Sparkles, Send, AlertTriangle, Loader2 } from 'lucide-react';
import { aiGuidanceResponses } from '../../data/mock';

const AIGuidanceModal = ({ open, onOpenChange, product }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const quickQuestions = [
    { id: 'how-it-helps', label: 'How does this help my condition?' },
    { id: 'when-to-use', label: 'When and how often should I use this?' },
    { id: 'safety-tips', label: 'What safety precautions should I know?' },
  ];

  const handleSendQuery = (questionId = null) => {
    const questionText = questionId
      ? quickQuestions.find((q) => q.id === questionId)?.label
      : query;

    if (!questionText?.trim()) return;

    setMessages((prev) => [...prev, { type: 'user', text: questionText }]);
    setQuery('');
    setIsLoading(true);

    // Simulate AI response delay
    setTimeout(() => {
      const responseKey = questionId || 'how-it-helps';
      const aiResponse = aiGuidanceResponses[responseKey] || aiGuidanceResponses['how-it-helps'];
      
      setMessages((prev) => [
        ...prev,
        {
          type: 'ai',
          text: aiResponse.replace('This product', product?.name || 'This product'),
        },
      ]);
      setIsLoading(false);
    }, 1200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span>AI Rehab Guide</span>
          </DialogTitle>
        </DialogHeader>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800">
            This is educational guidance only, not medical advice. Always consult your
            healthcare provider for personalized recommendations.
          </p>
        </div>

        {/* Product Context */}
        {product && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <img
              src={product.images?.[0] || 'https://placehold.co/60x60'}
              alt={product.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div>
              <p className="text-sm font-medium text-gray-900">Asking about:</p>
              <p className="text-sm text-gray-600">{product.name}</p>
            </div>
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto space-y-3 min-h-[200px] max-h-[300px] py-2">
          {messages.length === 0 ? (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Ask me anything about this product</p>
            </div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                    msg.type === 'user'
                      ? 'bg-red-600 text-white rounded-br-sm'
                      : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{msg.text}</p>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            </div>
          )}
        </div>

        {/* Quick Questions */}
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quick Questions</p>
            <div className="flex flex-wrap gap-2">
              {quickQuestions.map((q) => (
                <Button
                  key={q.id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendQuery(q.id)}
                  className="rounded-full text-sm border-gray-200 hover:border-red-300 hover:bg-red-50"
                >
                  {q.label}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendQuery()}
            placeholder="Type your question..."
            className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
          />
          <Button
            onClick={() => handleSendQuery()}
            disabled={!query.trim() || isLoading}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full w-10 h-10 p-0"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AIGuidanceModal;